import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import ConfirmDialog from "components/ConfirmDialog";
import { setMessage, useMaterialUIController } from "context";
import { IEventoResponse } from "interfaces/eventos";
import {
  CANAL_LABEL,
  ComunicadoCanal,
  ComunicadoCreatePayload,
  ComunicadoPublico,
  IContagemPublico,
  IVariavelComunicado,
  PUBLICO_LABEL,
} from "interfaces/comunicado";
import {
  contarPublicoComunicado,
  criarComunicado,
  obterFiltrosPublicoEvento,
  obterVariaveisComunicado,
  previewComunicado,
} from "services/comunicados";
import { getListUsers } from "services/users";
import ComunicadoVariaveisHelper from "./ComunicadoVariaveisHelper";
import {
  COMUNICADO_ACCENT,
  COMUNICADO_ACCENT_SOFT,
  composerContentSx,
  composerHeaderSx,
  composerPaperSx,
  sectionCardResponsiveSx,
} from "./comunicado-composer.theme";

const STEPS = [
  { id: "campanha", label: "Campanha" },
  { id: "publico", label: "Público" },
  { id: "titulos", label: "Títulos" },
  { id: "corpo", label: "Corpo" },
  { id: "revisar", label: "Revisar" },
] as const;

const STEP_REVISAR = STEPS.length - 1;

const CANAL_OPTIONS: ComunicadoCanal[] = ["EMAIL", "PUSH", "AMBOS"];
const PUBLICO_OPTIONS: ComunicadoPublico[] = [
  "TODOS_USUARIOS",
  "INSCRITOS_EVENTO",
  "INSCRITOS_CIDADE",
  "INSCRITOS_EQUIPE",
  "USUARIO_ESPECIFICO",
];

const CANAL_ICONS: Record<ComunicadoCanal, React.ReactNode> = {
  EMAIL: <EmailOutlinedIcon fontSize="small" />,
  PUSH: <NotificationsActiveOutlinedIcon fontSize="small" />,
  AMBOS: <LayersOutlinedIcon fontSize="small" />,
};

type UserOption = { id: string; nome: string; email: string };

function payloadParaApi(form: ComunicadoCreatePayload): ComunicadoCreatePayload {
  return {
    ...form,
    eventoId: form.eventoId ?? null,
    userId: form.userId ?? null,
    filtroCidade: form.filtroCidade?.length ? form.filtroCidade : null,
    filtroEquipe: form.filtroEquipe?.length ? form.filtroEquipe : null,
    assuntoEmail: form.assuntoEmail?.trim() || null,
    tituloPush: form.tituloPush?.trim() || null,
    urlImagePush: form.urlImagePush?.trim() || null,
  };
}

const emptyForm: ComunicadoCreatePayload = {
  tituloInterno: "",
  canal: "EMAIL",
  publico: "INSCRITOS_EVENTO",
  eventoId: null,
  userId: null,
  filtroCidade: [],
  filtroEquipe: [],
  assuntoEmail: "",
  tituloPush: "",
  corpoTemplate: "",
  urlImagePush: null,
};

type Props = {
  open: boolean;
  eventos: IEventoResponse[];
  onClose: () => void;
  onCreated: () => void;
};

function CompactStepBar({
  activeStep,
  isNarrow,
}: {
  activeStep: number;
  isNarrow: boolean;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ gap: { xs: 0.25, sm: 0.5 }, mt: 0.75 }}
    >
      {STEPS.map((stepItem, index) => {
        const done = index < activeStep;
        const active = index === activeStep;
        const showLabel = active || (!isNarrow && index < STEPS.length);

        return (
          <Stack key={stepItem.id} direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.65rem",
                fontWeight: 800,
                flexShrink: 0,
                bgcolor: done || active ? COMUNICADO_ACCENT : "rgba(255,255,255,0.18)",
                color: active ? "#fff" : done ? "#fff" : "rgba(255,255,255,0.75)",
                boxShadow: active ? `0 0 0 2px rgba(255,255,255,0.35)` : "none",
              }}
            >
              {index + 1}
            </Box>
            {showLabel ? (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: active ? 700 : 500,
                  color: active ? "#fff" : "rgba(255,255,255,0.65)",
                  whiteSpace: "nowrap",
                  display: { xs: active ? "block" : "none", sm: "block" },
                }}
              >
                {stepItem.label}
              </Typography>
            ) : null}
            {index < STEPS.length - 1 ? (
              <Box
                sx={{
                  width: { xs: 8, sm: 14 },
                  height: 2,
                  borderRadius: 1,
                  bgcolor: done ? COMUNICADO_ACCENT : "rgba(255,255,255,0.22)",
                  mx: 0.25,
                }}
              />
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
}

function AudiencePanel({
  contagem,
  loading,
  canal,
  compact = false,
}: {
  contagem: IContagemPublico | null;
  loading: boolean;
  canal: ComunicadoCanal;
  compact?: boolean;
}) {
  const cardSx = {
    ...sectionCardResponsiveSx,
    bgcolor: COMUNICADO_ACCENT_SOFT,
    borderColor: "rgba(249, 96, 48, 0.25)",
    height: compact ? "auto" : "100%",
  };

  if (compact) {
    return (
      <Box sx={cardSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
            <Icon sx={{ color: COMUNICADO_ACCENT, fontSize: "1.1rem" }}>groups</Icon>
            <MDTypography variant="button" fontWeight="bold">
              Destinatários
            </MDTypography>
          </Stack>

          {loading ? (
            <LinearProgress sx={{ borderRadius: 1, flex: 1, maxWidth: 200 }} color="warning" />
          ) : contagem ? (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="h5" fontWeight="bold" color={COMUNICADO_ACCENT}>
                {contagem.totalRegistros}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                no público
              </Typography>
              {(canal === "EMAIL" || canal === "AMBOS") && (
                <Chip
                  size="small"
                  icon={<EmailOutlinedIcon />}
                  label={`${contagem.comEmail} e-mails`}
                  sx={{ bgcolor: "background.paper" }}
                />
              )}
              {(canal === "PUSH" || canal === "AMBOS") && (
                <Chip
                  size="small"
                  icon={<NotificationsActiveOutlinedIcon />}
                  label={`${contagem.comPush} push`}
                  sx={{ bgcolor: "background.paper" }}
                />
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Defina o público para estimar destinatários.
            </Typography>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={cardSx}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
        <Icon sx={{ color: COMUNICADO_ACCENT, fontSize: "1.25rem" }}>groups</Icon>
        <MDTypography variant="button" fontWeight="bold">
          Destinatários estimados
        </MDTypography>
      </Stack>

      {loading ? (
        <LinearProgress sx={{ borderRadius: 1, mb: 1 }} color="warning" />
      ) : null}

      {contagem ? (
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="h4" fontWeight="bold" color={COMUNICADO_ACCENT}>
              {contagem.totalRegistros}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              pessoas no público selecionado
            </Typography>
          </Box>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {(canal === "EMAIL" || canal === "AMBOS") && (
              <Chip
                size="small"
                icon={<EmailOutlinedIcon />}
                label={`${contagem.comEmail} e-mails`}
                sx={{ bgcolor: "background.paper" }}
              />
            )}
            {(canal === "PUSH" || canal === "AMBOS") && (
              <Chip
                size="small"
                icon={<NotificationsActiveOutlinedIcon />}
                label={`${contagem.comPush} push`}
                sx={{ bgcolor: "background.paper" }}
              />
            )}
          </Stack>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Defina o público e os filtros para calcular quantas pessoas receberão
          esta campanha.
        </Typography>
      )}
    </Box>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{
        py: 0.85,
        borderBottom: "1px solid",
        borderColor: "grey.100",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ width: 108, flexShrink: 0, pt: 0.15 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ flex: 1, wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Stack>
  );
}

function PushPreviewMock({
  titulo,
  corpo,
  imageUrl,
}: {
  titulo: string;
  corpo: string;
  imageUrl?: string | null;
}) {
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "grey.200",
        bgcolor: "#f8f9fc",
        p: 1.5,
        boxShadow: "0 8px 24px rgba(26, 32, 53, 0.08)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.5,
            bgcolor: COMUNICADO_ACCENT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
          }}
        >
          <NotificationsActiveOutlinedIcon sx={{ fontSize: "1rem" }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" fontWeight={700} display="block">
            TNV Team Sports
          </Typography>
          <Typography variant="caption" color="text.secondary">
            agora
          </Typography>
        </Box>
      </Stack>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {titulo}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ whiteSpace: "pre-wrap", lineHeight: 1.45 }}
      >
        {corpo}
      </Typography>
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt=""
          sx={{
            mt: 1.25,
            width: "100%",
            maxHeight: 120,
            objectFit: "cover",
            borderRadius: 1.5,
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      ) : null}
    </Box>
  );
}

function EmailPreviewMock({ assunto, corpo }: { assunto: string; corpo: string }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "grey.200",
        overflow: "hidden",
        boxShadow: "0 8px 24px rgba(26, 32, 53, 0.08)",
      }}
    >
      <Box sx={{ px: 1.5, py: 1, bgcolor: "grey.100", borderBottom: "1px solid", borderColor: "grey.200" }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <EmailOutlinedIcon sx={{ fontSize: "1rem", color: COMUNICADO_ACCENT }} />
          <Typography variant="caption" fontWeight={700}>
            Prévia do e-mail
          </Typography>
        </Stack>
      </Box>
      <Box sx={{ p: 1.5, bgcolor: "#fff" }}>
        <Typography variant="caption" color="text.secondary" display="block">
          Assunto
        </Typography>
        <Typography variant="body2" fontWeight={700} mb={1.25}>
          {assunto}
        </Typography>
        <Divider sx={{ mb: 1.25 }} />
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
          {corpo}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ComunicadoComposerDrawer({
  open,
  eventos,
  onClose,
  onCreated,
}: Props) {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [, dispatch] = useMaterialUIController();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<ComunicadoCreatePayload>(emptyForm);
  const [variaveis, setVariaveis] = useState<IVariavelComunicado[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [filtros, setFiltros] = useState<{ cidades: string[]; equipes: string[] }>({
    cidades: [],
    equipes: [],
  });
  const [contagem, setContagem] = useState<IContagemPublico | null>(null);
  const [preview, setPreview] = useState<{
    assuntoEmail: string | null;
    tituloPush: string | null;
    corpo: string;
  } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingContagem, setLoadingContagem] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const precisaEvento = form.publico === "INSCRITOS_EVENTO";
  const incluiEmail = form.canal === "EMAIL" || form.canal === "AMBOS";
  const incluiPush = form.canal === "PUSH" || form.canal === "AMBOS";

  const eventoSelected = useMemo(
    () => eventos.find((e) => e.id === form.eventoId) ?? null,
    [eventos, form.eventoId],
  );

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setForm(emptyForm);
    setPreview(null);
    setContagem(null);
    obterVariaveisComunicado()
      .then(setVariaveis)
      .catch(() => setVariaveis([]));
    getListUsers()
      .then((res) => setUsers(res ?? []))
      .catch(() => setUsers([]));
    obterFiltrosPublicoEvento()
      .then(setFiltros)
      .catch(() => setFiltros({ cidades: [], equipes: [] }));
  }, [open]);

  const refreshContagem = useCallback(async () => {
    if (precisaEvento && !form.eventoId) {
      setContagem(null);
      return;
    }
    if (form.publico === "INSCRITOS_CIDADE" && !form.filtroCidade?.length) {
      setContagem(null);
      return;
    }
    if (form.publico === "INSCRITOS_EQUIPE" && !form.filtroEquipe?.length) {
      setContagem(null);
      return;
    }
    if (form.publico === "USUARIO_ESPECIFICO" && !form.userId) {
      setContagem(null);
      return;
    }

    setLoadingContagem(true);
    try {
      const data = await contarPublicoComunicado({
        publico: form.publico,
        canal: form.canal,
        eventoId: form.eventoId ?? undefined,
        userId: form.userId ?? undefined,
        filtroCidade: form.filtroCidade?.join(", ") || undefined,
        filtroEquipe: form.filtroEquipe?.join(", ") || undefined,
      });
      setContagem(data);
    } catch {
      setContagem(null);
    } finally {
      setLoadingContagem(false);
    }
  }, [form, precisaEvento]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(refreshContagem, 400);
    return () => clearTimeout(t);
  }, [open, refreshContagem]);

  const handleInsertVariavel = (chave: string) => {
    setForm((prev) => ({
      ...prev,
      corpoTemplate: `${prev.corpoTemplate}{{${chave}}}`,
    }));
  };

  const validateStep = (s: number): string | null => {
    if (s === 0) {
      if (!form.tituloInterno.trim()) return "Informe um nome para identificar a campanha.";
      return null;
    }
    if (s === 1) {
      if (precisaEvento && !form.eventoId) return "Selecione o evento.";
      if (form.publico === "INSCRITOS_CIDADE" && !form.filtroCidade?.length) {
        return "Selecione ao menos uma cidade.";
      }
      if (form.publico === "INSCRITOS_EQUIPE" && !form.filtroEquipe?.length) {
        return "Selecione ao menos uma equipe.";
      }
      if (form.publico === "USUARIO_ESPECIFICO" && !form.userId) {
        return "Selecione o usuário destinatário.";
      }
      return null;
    }
    if (s === 2) {
      if (incluiEmail && !form.assuntoEmail?.trim()) {
        return "Informe o assunto do e-mail.";
      }
      if (incluiPush && !form.tituloPush?.trim()) {
        return "Informe o título da notificação push.";
      }
      return null;
    }
    if (s === 3) {
      if (!form.corpoTemplate.trim()) return "Escreva o corpo da mensagem.";
      return null;
    }
    return null;
  };

  const validateAll = (): string | null => {
    for (let i = 0; i < STEP_REVISAR; i++) {
      const err = validateStep(i);
      if (err) return err;
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep(step);
    if (err) {
      setMessage(dispatch, { open: true, severity: "error", text: err });
      return;
    }
    if (step === STEP_REVISAR - 1) {
      void handlePreview();
    }
    setStep((s) => Math.min(s + 1, STEP_REVISAR));
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const handlePreview = async () => {
    if (!form.corpoTemplate.trim()) return;
    setLoadingPreview(true);
    try {
      const data = await previewComunicado({
        corpoTemplate: form.corpoTemplate,
        assuntoEmail: form.assuntoEmail,
        tituloPush: form.tituloPush,
        eventoId: form.eventoId ?? undefined,
        userId: form.userId ?? undefined,
      });
      setPreview({
        assuntoEmail: data.assuntoEmail,
        tituloPush: data.tituloPush,
        corpo: data.corpo,
      });
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: error instanceof Error ? error.message : "Não foi possível gerar a prévia.",
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSubmit = () => {
    const err = validateAll();
    if (err) {
      setMessage(dispatch, { open: true, severity: "error", text: err });
      return;
    }
    setConfirmOpen(true);
  };

  const confirmSend = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const result = await criarComunicado(payloadParaApi(form));
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: result.message || "Campanha enfileirada com sucesso.",
      });
      onCreated();
      onClose();
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: error instanceof Error ? error.message : "Falha ao enviar a campanha.",
      });
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            ...composerPaperSx,
            width: { xs: "100%", sm: "96vw" },
            maxWidth: 1280,
          },
        }}
      >
        <Box sx={composerHeaderSx}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={800} lineHeight={1.2}>
                Nova campanha
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.75, display: { xs: "none", md: "block" } }}
              >
                {STEPS[step]?.label} — passo {step + 1} de {STEPS.length}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: "inherit", flexShrink: 0 }}
              aria-label="Fechar"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <CompactStepBar activeStep={step} isNarrow={isNarrow} />
        </Box>

        <DialogContent sx={{ p: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <MDBox sx={composerContentSx}>
            <Grid container spacing={{ xs: 1.5, md: 2.5 }}>
              {step < STEP_REVISAR && isCompact ? (
                <Grid item xs={12} sx={{ order: -1 }}>
                  <AudiencePanel
                    contagem={contagem}
                    loading={loadingContagem}
                    canal={form.canal}
                    compact
                  />
                </Grid>
              ) : null}

              <Grid item xs={12} lg={step < STEP_REVISAR ? 8 : 12}>
                {step === 0 && (
                  <Grid container spacing={{ xs: 1.5, md: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ ...sectionCardResponsiveSx, height: "100%" }}>
                        <MDTypography variant="button" fontWeight="bold" mb={1.5} display="block">
                          Identificação
                        </MDTypography>
                        <TextField
                          fullWidth
                          size="small"
                          label="Nome interno da campanha"
                          placeholder="Ex.: Lembrete kit — Corrida Limeira"
                          value={form.tituloInterno}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, tituloInterno: e.target.value }))
                          }
                          helperText="Só aparece no painel — o destinatário não vê este nome."
                          sx={fieldSx}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box sx={{ ...sectionCardResponsiveSx, height: "100%" }}>
                        <MDTypography variant="button" fontWeight="bold" mb={1.5} display="block">
                          Canal de envio
                        </MDTypography>
                        <ToggleButtonGroup
                          exclusive
                          fullWidth
                          orientation={isNarrow ? "vertical" : "horizontal"}
                          value={form.canal}
                          onChange={(_, v: ComunicadoCanal | null) =>
                            v && setForm((p) => ({ ...p, canal: v }))
                          }
                          sx={{
                            "& .MuiToggleButton-root": {
                              py: 1,
                              textTransform: "none",
                              gap: 0.75,
                              borderRadius: "8px !important",
                              "&.Mui-selected": {
                                bgcolor: COMUNICADO_ACCENT_SOFT,
                                borderColor: COMUNICADO_ACCENT,
                                color: COMUNICADO_ACCENT,
                                fontWeight: 600,
                              },
                            },
                          }}
                        >
                          {CANAL_OPTIONS.map((c) => (
                            <ToggleButton key={c} value={c}>
                              {CANAL_ICONS[c]}
                              {CANAL_LABEL[c]}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {step === 1 && (
                  <Box sx={sectionCardResponsiveSx}>
                    <MDTypography variant="button" fontWeight="bold" mb={1.5} display="block">
                      Público-alvo
                    </MDTypography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={PUBLICO_OPTIONS}
                          value={form.publico}
                          getOptionLabel={(o) => PUBLICO_LABEL[o]}
                          onChange={(_, v) =>
                            v &&
                            setForm((p) => ({
                              ...p,
                              publico: v,
                              eventoId: null,
                              userId: null,
                              filtroCidade: [],
                              filtroEquipe: [],
                            }))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Quem recebe"
                              size="small"
                              sx={fieldSx}
                            />
                          )}
                        />
                      </Grid>

                      {precisaEvento ? (
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            options={eventos}
                            value={eventoSelected}
                            getOptionLabel={(o) => o.titulo}
                            onChange={(_, v) =>
                              setForm((p) => ({
                                ...p,
                                eventoId: v?.id ?? null,
                                filtroCidade: [],
                                filtroEquipe: [],
                              }))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Evento"
                                placeholder="Buscar evento..."
                                size="small"
                                sx={fieldSx}
                              />
                            )}
                          />
                        </Grid>
                      ) : null}

                      {form.publico === "USUARIO_ESPECIFICO" ? (
                        <Grid item xs={12} sm={precisaEvento ? 12 : 6}>
                          <Autocomplete
                            options={users}
                            value={users.find((u) => u.id === form.userId) ?? null}
                            getOptionLabel={(o) => `${o.nome} · ${o.email}`}
                            onChange={(_, v) =>
                              setForm((p) => ({ ...p, userId: v?.id ?? null }))
                            }
                            renderInput={(params) => (
                              <TextField {...params} label="Usuário" size="small" sx={fieldSx} />
                            )}
                          />
                        </Grid>
                      ) : null}

                      {form.publico === "INSCRITOS_CIDADE" ? (
                        <Grid item xs={12}>
                          <Autocomplete
                            options={filtros.cidades}
                            multiple
                            value={form.filtroCidade ?? []}
                            onChange={(_, v) => setForm((p) => ({ ...p, filtroCidade: v }))}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Cidades"
                                placeholder="Selecione uma ou mais"
                                size="small"
                                sx={fieldSx}
                              />
                            )}
                          />
                        </Grid>
                      ) : null}

                      {form.publico === "INSCRITOS_EQUIPE" ? (
                        <Grid item xs={12}>
                          <Autocomplete
                            options={filtros.equipes}
                            multiple
                            value={form.filtroEquipe ?? []}
                            onChange={(_, v) => setForm((p) => ({ ...p, filtroEquipe: v }))}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Equipes"
                                placeholder="Selecione uma ou mais"
                                size="small"
                                sx={fieldSx}
                              />
                            )}
                          />
                        </Grid>
                      ) : null}
                    </Grid>
                  </Box>
                )}

                {step === 2 && (
                  <Stack spacing={{ xs: 1.5, md: 2 }}>
                    {!incluiEmail && !incluiPush ? (
                      <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                        Selecione um canal de envio no passo Campanha.
                      </Alert>
                    ) : null}

                    <Grid container spacing={{ xs: 1.5, md: 2 }}>
                      {incluiEmail ? (
                        <Grid item xs={12} md={incluiPush ? 6 : 12}>
                          <Box sx={{ ...sectionCardResponsiveSx, height: "100%" }}>
                            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                              <EmailOutlinedIcon sx={{ color: COMUNICADO_ACCENT }} />
                              <MDTypography variant="button" fontWeight="bold">
                                Assunto do e-mail
                              </MDTypography>
                            </Stack>
                            <TextField
                              fullWidth
                              size="small"
                              label="Assunto"
                              placeholder="Ex.: Seu kit está disponível para retirada"
                              value={form.assuntoEmail ?? ""}
                              onChange={(e) =>
                                setForm((p) => ({ ...p, assuntoEmail: e.target.value }))
                              }
                              sx={fieldSx}
                            />
                          </Box>
                        </Grid>
                      ) : null}

                      {incluiPush ? (
                        <Grid item xs={12} md={incluiEmail ? 6 : 12}>
                          <Box sx={{ ...sectionCardResponsiveSx, height: "100%" }}>
                            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                              <NotificationsActiveOutlinedIcon sx={{ color: COMUNICADO_ACCENT }} />
                              <MDTypography variant="button" fontWeight="bold">
                                Notificação push
                              </MDTypography>
                            </Stack>
                            <Stack spacing={1.5}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Título da notificação"
                                placeholder="Ex.: Kit disponível!"
                                value={form.tituloPush ?? ""}
                                onChange={(e) =>
                                  setForm((p) => ({ ...p, tituloPush: e.target.value }))
                                }
                                sx={fieldSx}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                label="URL da imagem (opcional)"
                                value={form.urlImagePush ?? ""}
                                onChange={(e) =>
                                  setForm((p) => ({
                                    ...p,
                                    urlImagePush: e.target.value || null,
                                  }))
                                }
                                sx={fieldSx}
                              />
                            </Stack>
                          </Box>
                        </Grid>
                      ) : null}
                    </Grid>
                  </Stack>
                )}

                {step === 3 && (
                  <Box sx={sectionCardResponsiveSx}>
                    <MDTypography variant="button" fontWeight="bold" mb={0.5} display="block">
                      Corpo da mensagem
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mb={1}>
                      Use variáveis dinâmicas para personalizar nome, evento e links.
                    </MDTypography>
                    <ComunicadoVariaveisHelper
                      variaveis={variaveis}
                      onInsert={handleInsertVariavel}
                    />
                    <TextField
                      fullWidth
                      multiline
                      minRows={isCompact ? 6 : 8}
                      maxRows={isCompact ? 14 : 18}
                      placeholder="Olá {{nome_atleta}}, sua inscrição em {{nome_evento}} está confirmada..."
                      value={form.corpoTemplate}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, corpoTemplate: e.target.value }))
                      }
                      sx={{ mt: 1.5, ...fieldSx }}
                    />
                  </Box>
                )}

                {step === STEP_REVISAR && (
                  <Stack spacing={{ xs: 1.5, md: 2 }}>
                    <AudiencePanel
                      contagem={contagem}
                      loading={loadingContagem}
                      canal={form.canal}
                      compact
                    />

                    <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="stretch">
                      <Grid item xs={12} md={5}>
                        <Box sx={{ ...sectionCardResponsiveSx, height: "100%" }}>
                          <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                            Resumo
                          </MDTypography>
                          <Typography variant="caption" color="text.secondary" display="block" mb={1.25}>
                            Confira os dados antes de enfileirar o envio.
                          </Typography>

                          <Stack direction="row" flexWrap="wrap" gap={0.75} mb={1.5}>
                            <Chip
                              size="small"
                              icon={
                                form.canal === "EMAIL" ? (
                                  <EmailOutlinedIcon />
                                ) : form.canal === "PUSH" ? (
                                  <NotificationsActiveOutlinedIcon />
                                ) : (
                                  <LayersOutlinedIcon />
                                )
                              }
                              label={CANAL_LABEL[form.canal]}
                              sx={{ bgcolor: COMUNICADO_ACCENT_SOFT, fontWeight: 600 }}
                            />
                            <Chip
                              size="small"
                              label={PUBLICO_LABEL[form.publico]}
                              variant="outlined"
                            />
                          </Stack>

                          <SummaryRow label="Campanha" value={form.tituloInterno} />
                          {eventoSelected ? (
                            <SummaryRow label="Evento" value={eventoSelected.titulo} />
                          ) : null}
                          {form.assuntoEmail?.trim() ? (
                            <SummaryRow label="Assunto" value={form.assuntoEmail} />
                          ) : null}
                          {form.tituloPush?.trim() ? (
                            <SummaryRow label="Título push" value={form.tituloPush} />
                          ) : null}
                          {contagem ? (
                            <SummaryRow
                              label="Destinatários"
                              value={`${contagem.totalRegistros} pessoa(s)`}
                            />
                          ) : null}
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={7}>
                        <Box
                          sx={{
                            ...sectionCardResponsiveSx,
                            height: "100%",
                            bgcolor: "grey.50",
                          }}
                        >
                          <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                            Como o destinatário verá
                          </MDTypography>

                          {loadingPreview ? (
                            <LinearProgress sx={{ borderRadius: 1, my: 2 }} />
                          ) : preview ? (
                            <Stack spacing={1.5}>
                              {(form.canal === "PUSH" || form.canal === "AMBOS") &&
                              preview.tituloPush ? (
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={600}
                                    display="block"
                                    mb={0.75}
                                  >
                                    Notificação push
                                  </Typography>
                                  <PushPreviewMock
                                    titulo={preview.tituloPush}
                                    corpo={preview.corpo}
                                    imageUrl={form.urlImagePush}
                                  />
                                </Box>
                              ) : null}

                              {(form.canal === "EMAIL" || form.canal === "AMBOS") &&
                              preview.assuntoEmail ? (
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={600}
                                    display="block"
                                    mb={0.75}
                                  >
                                    E-mail
                                  </Typography>
                                  <EmailPreviewMock
                                    assunto={preview.assuntoEmail}
                                    corpo={preview.corpo}
                                  />
                                </Box>
                              ) : null}

                              {form.canal === "PUSH" && !preview.tituloPush ? (
                                <PushPreviewMock
                                  titulo={form.tituloPush || "Sem título"}
                                  corpo={preview.corpo}
                                  imageUrl={form.urlImagePush}
                                />
                              ) : null}

                              <Typography variant="caption" color="text.secondary">
                                Prévia com dados de exemplo do evento ou destinatário selecionado.
                              </Typography>
                            </Stack>
                          ) : (
                            <Alert severity="warning" sx={{ borderRadius: 1.5, mt: 1 }}>
                              Não foi possível carregar a prévia. Volte ao passo anterior e tente
                              novamente.
                            </Alert>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Stack>
                )}
              </Grid>

              {step < STEP_REVISAR && !isCompact ? (
                <Grid item xs={12} lg={4}>
                  <Box sx={{ position: "sticky", top: 0 }}>
                    <AudiencePanel
                      contagem={contagem}
                      loading={loadingContagem}
                      canal={form.canal}
                    />
                  </Box>
                </Grid>
              ) : null}
            </Grid>
          </MDBox>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 1.25, md: 2 },
            borderTop: "1px solid",
            borderColor: "grey.200",
            bgcolor: "grey.50",
            justifyContent: "space-between",
            flexShrink: 0,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <MDButton variant="text" color="dark" onClick={onClose} disabled={saving}>
            Cancelar
          </MDButton>

          <Stack direction="row" spacing={1}>
            {step > 0 && (
              <MDButton variant="outlined" color="dark" onClick={handleBack} disabled={saving}>
                Voltar
              </MDButton>
            )}
            {step < STEP_REVISAR ? (
              <MDButton variant="gradient" color="dark" onClick={handleNext}>
                Continuar
              </MDButton>
            ) : (
              <MDButton
                variant="gradient"
                color="warning"
                onClick={handleSubmit}
                disabled={saving}
                sx={{
                  background: `linear-gradient(195deg, ${COMUNICADO_ACCENT}, #e04a1a)`,
                }}
              >
                <Icon sx={{ mr: 0.5, fontSize: "1.1rem !important" }}>send</Icon>
                Enfileirar campanha
              </MDButton>
            )}
          </Stack>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        title="Enfileirar campanha"
        message={`Confirma o envio desta campanha?${
          contagem
            ? ` Aproximadamente ${contagem.totalRegistros} destinatários serão processados.`
            : ""
        }`}
        confirmLabel="Enfileirar"
        destructive={false}
        onConfirm={confirmSend}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}
